import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';

// GoA's goa-link renders its leading/trailing goa-icon as a div[role="img"] with an empty
// aria-label plus stray aria-expanded/aria-controls attributes, which axe flags as "must have
// alternative text" and "must only use supported ARIA attributes". The icon is purely
// decorative next to the link's own visible text, so we drop the redundant role/label and the
// stray attributes once the shadow DOM has rendered.
// Usage: Add the linkIconAccessibility directive to any goab-link element that uses
// leadingIcon/trailingIcon.
@Directive({
  selector: '[linkIconAccessibility]',
  standalone: true,
})
export class LinkIconAccessibilityDirective
  implements AfterViewInit, OnDestroy
{
  private observer!: MutationObserver;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      const host = this.el.nativeElement.querySelector('goa-link')?.shadowRoot;
      if (!host) {
        return;
      }

      const targets = Array.from(host.querySelectorAll('goa-icon'))
        .map(
          (icon) =>
            icon.shadowRoot?.querySelector(
              'div[role="img"]'
            ) as HTMLElement | null
        )
        .filter((target): target is HTMLElement => !!target);

      if (targets.length === 0) {
        return;
      }

      this.observer = new MutationObserver(() => {
        targets.forEach((target) => this.fixAttributes(target));
      });

      targets.forEach((target) => {
        this.fixAttributes(target);
        this.observer.observe(target, {
          attributes: true,
          attributeFilter: [
            'aria-label',
            'role',
            'aria-expanded',
            'aria-controls',
          ],
        });
      });
    }, 0);
  }

  private fixAttributes(element: HTMLElement): void {
    this.renderer.removeAttribute(element, 'role');
    this.renderer.removeAttribute(element, 'aria-label');
    this.renderer.removeAttribute(element, 'aria-expanded');
    this.renderer.removeAttribute(element, 'aria-controls');
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
